import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListSalesOrdersInput } from "@nexxt/contracts/sales";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the sales orders (pattern.md §10): one factory, the same keys for loaders, screens and mutations.
// Lines, the state towards Business Central and the history of an order keep their keys under `of`, so whatever
// reloads the orders reloads them too.

const orders = api.sales["sales-orders"];

type ListPage = InferResponseType<typeof orders.$get, 200>;
export type SalesOrder = ListPage["items"][number];
export type SalesOrderOptions = InferResponseType<(typeof api.sales)["sales-order-options"]["$get"], 200>;
/** A lot of an order (ADR-195): what one lock froze, its shipment, where its process stands. */
export type SalesOrderLot = InferResponseType<(typeof orders)[":salesOrderId"]["lots"]["$get"], 200>[number];

/** The filters of the list input (`listSalesOrdersInput`), without the paging the queries add. */
export type SalesOrderFilters = Pick<
  ListSalesOrdersInput,
  "status" | "customer" | "brand" | "season" | "bcSyncStatus" | "confirmation" | "q"
> & {
  joorChangePending?: boolean | undefined;
  lockable?: boolean | undefined;
  creditBlocked?: boolean | undefined;
  awaitingPayment?: boolean | undefined;
  depositDue?: boolean | undefined;
  fulfillmentIssue?: boolean | undefined;
  uncovered?: boolean | undefined;
  dunningOver?: boolean | undefined;
  withoutSeason?: boolean | undefined;
  withoutConfirmation?: boolean | undefined;
  /** The sets of the queues "To do", as `Todo` writes them (ADR-302). */
  anyOf?: string | undefined;
};

/** The tabs of the page of an order, in the order it shows them. */
export const salesOrderTabs = [
  "lines",
  "allocation",
  "details",
  "credit",
  "documents",
  "confirmations",
  "shipment",
  "claims",
  "sync",
  "history",
] as const;
export type SalesOrderTab = (typeof salesOrderTabs)[number];

const PAGE_SIZE = 50;

const keys = {
  all: ["sales-orders"] as const,
  /** Prefix of every cached list: a write puts the order it gets back in each of them. */
  lists: ["sales-orders", "list"] as const,
  /** Prefix of what is cached of one order besides its header: lines, sync, history. */
  of: (salesOrderId: string) => ["sales-orders", "of", salesOrderId] as const,
};

export const salesOrderQueries = {
  ...keys,

  list: (filters: SalesOrderFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          orders.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** By id or by number. */
  detail: (key: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", key],
      queryFn: ({ signal }) => unwrap(orders[":key"].$get({ param: { key } }, { init: { signal } })),
    }),

  history: (salesOrderId: string) =>
    infiniteQueryOptions({
      queryKey: [...keys.of(salesOrderId), "history"],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          orders[":salesOrderId"].history.$get(
            { param: { salesOrderId }, query: pageParam ? { cursor: pageParam } : {} },
            { init: { signal } },
          ),
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),

  /** Its lots, by number (ADR-195): none before its lock, nor for an order locked before the lots. */
  lots: (salesOrderId: string) =>
    queryOptions({
      queryKey: [...keys.of(salesOrderId), "lots"],
      queryFn: ({ signal }) =>
        unwrap(orders[":salesOrderId"].lots.$get({ param: { salesOrderId } }, { init: { signal } })),
    }),

  /** The values of the filters and the warehouses (ADR-048, ADR-122): they change rarely. */
  options: () =>
    queryOptions({
      queryKey: [...keys.all, "options"],
      queryFn: ({ signal }) => unwrap(api.sales["sales-order-options"].$get({}, { init: { signal } })),
      staleTime: 10 * 60_000,
    }),
};
